import {
  collection,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  getDocFromServer,
  getDocsFromServer,
  query,
  where
} from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { ref, uploadString, getBytes, deleteObject } from 'firebase/storage'
import { db, functions, storage } from '@/firebase'
import {
  createFixtureSession,
  fixtureSessionBlockReason,
  withFixtureSession
} from '../fixtureSession'
import { probe } from '../ruleExpectations'

const check = (ok, message) => {
  if (!ok) throw new Error(message)
}
const pause = (ms) => new Promise((resolve) => setTimeout(resolve, ms))
const hash = async (text) =>
  Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text))))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')

export default {
  id: 'saleReadiness',
  label: 'Sale readiness: recovery, cleanup and licensing',
  description:
    'Real server denials, school restore/readback/retry, feedback deletion triggers, and concurrent last-school enforcement. Synthetic data only; results save automatically.',
  destructive: true,
  oncePerSweep: true,
  async run(ctx, step) {
    const reason = fixtureSessionBlockReason(ctx)
    if (reason) {
      step.block('sale readiness fixtures', reason)
      return
    }
    const io = createFixtureSession(ctx)
    const tag = { __smokeTest: true, __smokeRunId: ctx.runId }
    const key = doc(collection(db, 'backups')).id
    const backup = doc(db, 'backups', `smoke_${key}`)
    const config = doc(db, 'config', `smoke_${key}`)
    const file = ref(storage, `admin/backups/smoke_${key}.json`)
    const forms = [],
      responses = [],
      privateDocs = []
    const call = async (name, data) => (await httpsCallable(functions, name)(data)).data
    await withFixtureSession(ctx, step, io, async () => {
      try {
        await step('server denies direct school creation, including District Admin', () =>
          probe(false, () =>
            setDoc(doc(db, 'schools', `dev-smoke-${key}`), { name: 'SMOKE direct create', ...tag })
          )
        )
        await step('server enforces missing license and concurrent last-school limit', async () => {
          const r = await call('probeSchoolEntitlements', { runId: ctx.runId })
          check(
            r.missingLicenseDenied &&
              r.concurrentWinners === 1 &&
              r.limitDenials === 1 &&
              r.schoolsCreated === 1,
            'Entitlement probe did not verify all boundaries'
          )
          return `Missing license denied; exactly one concurrent request succeeded. Reference ${r.operationId}`
        })
        await step('District Admin can save and read synthetic backup and config', async () => {
          await setDoc(backup, { ...tag, name: 'SMOKE readiness' })
          await setDoc(config, tag)
          await uploadString(file, '{"synthetic":true}', 'raw', { contentType: 'application/json' })
          check((await getDocFromServer(backup)).exists(), 'Backup metadata missing')
          check(
            new TextDecoder().decode(await getBytes(file)) === '{"synthetic":true}',
            'Backup bytes differ'
          )
          return 'Metadata, file bytes and disposable config write verified'
        })
        for (const role of ['school_admin', 'admin_504']) {
          await step(`verify ${role} JWT`, () => io.as({ role, allSchools: false }))
          for (const [name, fn] of [
            ['read district backup metadata', () => getDocFromServer(backup)],
            ['overwrite district backup metadata', () => setDoc(backup, tag)],
            ['download district backup file', () => getBytes(file)],
            ['overwrite district backup file', () => uploadString(file, 'forbidden')],
            ['write shared config', () => setDoc(config, tag)]
          ])
            await step(`${role} cannot ${name}`, () => probe(false, fn))
        }
        await step('return to District Admin for restore and lifecycle checks', () =>
          io.as({ role: 'district_admin', allSchools: false })
        )
        let a, b, preview
        const student = (name) => ({
          app: {
            studentData: {
              firstName: 'SMOKE',
              lastName: name,
              plan: '504',
              caseManagerId: ctx.user.uid
            },
            accommodations: { instruction: 'before restore' }
          }
        })
        await step('prepare two-campus restore controls and private contacts', async () => {
          a = await io.student(0, student('restore'))
          b = await io.student(1, student('control'))
          const privateRef = doc(db, 'studentPrivate', a.id)
          privateDocs.push(privateRef)
          await setDoc(privateRef, {
            studentId: a.id,
            schoolId: io.schools[0].id,
            guardianContacts: [{ name: 'SMOKE before' }]
          })
          await call('refreshStudentAccess', { studentId: a.id })
          await call('refreshStudentAccess', { studentId: b.id })
          return 'Only disposable students and synthetic contacts created'
        })
        const backupJson = JSON.stringify({
          metadata: { projectId: 'casemangervue' },
          collections: {
            students: [a, b].map((r, i) => ({
              id: r.id,
              schoolId: io.schools[i].id,
              ...student('restored'),
              ...tag
            })),
            studentPrivate: [
              {
                id: a.id,
                studentId: a.id,
                schoolId: io.schools[0].id,
                guardianContacts: [{ name: 'SMOKE restored' }]
              }
            ]
          }
        })
        const previewArgs = { schoolId: io.schools[0].id, mode: 'replace-existing', backupJson }
        await step(
          'preview reports one selected-school replacement and excludes other campus',
          async () => {
            preview = await call('previewSchoolRestore', previewArgs)
            check(
              preview.canApply && preview.counts.replace === 1 && preview.otherSchoolStudents === 1,
              'Restore preview scope differs'
            )
            check(
              (await getDocFromServer(a)).data().app.studentData.lastName === 'restore',
              'Preview modified live data'
            )
            return `One replacement; one other-school student excluded. Reference ${preview.operationId}`
          }
        )
        await step(
          'stale restore preview is rejected without overwriting an intervening edit',
          async () => {
            await updateDoc(a, { 'app.accommodations.instruction': 'intervening edit' })
            let denied = false
            try {
              await call('applySchoolRestore', {
                jobId: preview.jobId,
                confirmText: preview.schoolName
              })
            } catch (error) {
              if (error.code !== 'functions/failed-precondition') throw error
              denied = true
            }
            check(
              denied &&
                (await getDocFromServer(a)).data().app.accommodations.instruction ===
                  'intervening edit',
              'Stale preview overwrote newer data'
            )
            return 'Server rejected stale preview; newer data preserved'
          }
        )
        await step(
          'fresh restore commits selected student and contacts; retry does not apply twice',
          async () => {
            preview = await call('previewSchoolRestore', previewArgs)
            const args = { jobId: preview.jobId, confirmText: preview.schoolName }
            const first = await call('applySchoolRestore', args)
            const retry = await call('applySchoolRestore', args)
            check(
              first.alreadyApplied === false && retry.alreadyApplied === true,
              'Restore retry was not idempotent'
            )
            check(
              (await getDocFromServer(a)).data().app.studentData.lastName === 'restored',
              'Selected student readback differs'
            )
            check(
              (await getDocFromServer(privateDocs[0])).data().guardianContacts[0].name ===
                'SMOKE restored',
              'Private contact readback differs'
            )
            check(
              (await getDocFromServer(b)).data().app.studentData.lastName === 'control',
              'Other campus was modified'
            )
            const recovery = JSON.parse(
              (await call('downloadSchoolRestoreRecovery', { jobId: preview.jobId })).backupJson
            )
            check(
              recovery.collections.students[0].app.accommodations.instruction ===
                'intervening edit',
              'Recovery copy lost the pre-restore record'
            )
            return `Student, contacts, other-campus control and recovery copy verified. Reference ${first.operationId}`
          }
        )
        await step('create feedback deletion controls without sending email', async () => {
          for (const [index, studentId] of [a.id, b.id, null].entries()) {
            const f = doc(collection(db, 'feedbackInstances'))
            forms.push(f)
            await setDoc(f, {
              ...tag,
              studentId,
              schoolId: io.schools[index === 1 ? 1 : 0].id,
              caseManagerId: ctx.user.uid,
              audienceType: studentId ? 'student' : 'general',
              assignedTeachers: [ctx.user.uid],
              status: 'pending',
              autoReminders: { enabled: false }
            })
            for (const status of ['pending', 'submitted']) {
              const r = doc(collection(db, 'feedbackResponses'))
              responses.push(r)
              await setDoc(r, {
                ...tag,
                instanceId: f.id,
                studentId,
                schoolId: io.schools[index === 1 ? 1 : 0].id,
                teacherId: ctx.user.uid,
                status,
                responses: {}
              })
            }
          }
          return 'Three forms and six responses; reminders disabled'
        })
        // Delete contacts while the parent exists; the app intentionally does
        // not promise a cascade for retained private/history/file records.
        await deleteDoc(privateDocs[0])
        privateDocs.length = 0
        await step(
          'student deletion triggers cleanup, records success and preserves controls',
          async () => {
            await deleteDoc(a)
            // The lifecycle probe deliberately removed this parent. Its private
            // fixture is already gone; do not query/delete child history through
            // parent-dependent client rules during the shared final cleanup.
            io.students.splice(io.students.indexOf(a), 1)
            const subject = await hash(`student:${a.id}`)
            const deadline = Date.now() + 90000
            let evidence
            do {
              const rows = await getDocsFromServer(
                query(collection(db, 'feedbackCleanupRuns'), where('__smokeRunId', '==', ctx.runId))
              )
              evidence = rows.docs
                .map((d) => d.data())
                .find((d) => d.kind === 'student' && d.subjectHash === subject)
              if (evidence?.status === 'completed') break
              if (evidence && ['needs_review', 'stopped_parent_restored'].includes(evidence.status))
                throw new Error(`Cleanup status: ${evidence.status}`)
              await pause(2000)
            } while (Date.now() < deadline)
            check(
              evidence?.status === 'completed',
              'Cleanup did not finish within 90 seconds; inspect feedbackCleanupRuns before retrying'
            )
            const remainingForms = await getDocsFromServer(
              query(collection(db, 'feedbackInstances'), where('studentId', '==', a.id))
            )
            const remainingResponses = await getDocsFromServer(
              query(collection(db, 'feedbackResponses'), where('studentId', '==', a.id))
            )
            check(
              remainingForms.empty && remainingResponses.empty,
              'Linked feedback remains after completion'
            )
            for (const control of [...forms.slice(1), ...responses.slice(2)])
              check((await getDocFromServer(control)).exists(), 'Unrelated feedback was deleted')
            return 'Actual trigger completed; zero linked forms/responses remain; other-campus and general-survey controls preserved'
          }
        )
        await step('late feedback cannot recreate a deleted student form', () =>
          probe(false, () =>
            setDoc(forms[0], {
              ...tag,
              schoolId: io.schools[0].id,
              studentId: a.id,
              caseManagerId: ctx.user.uid,
              assignedTeachers: [],
              status: 'pending'
            })
          )
        )
      } finally {
        await step(
          'remove readiness files, contacts and disposable feedback controls',
          async () => {
            await io.as({ role: 'district_admin', allSchools: false })
            const results = await Promise.allSettled(
              [...privateDocs, ...responses, ...forms, backup, config].map((r) => deleteDoc(r))
            )
            const storageResult = await deleteObject(file).catch((e) => {
              if (e.code !== 'storage/object-not-found') throw e
            })
            void storageResult
            // Missing already-cleaned form documents may be denied by the
            // parent-scoped rules; the Admin SDK leftover sweep removes tags.
            const errors = results.filter(
              (r) => r.status === 'rejected' && r.reason.code !== 'permission-denied'
            )
            check(!errors.length, 'Fixture cleanup failed; use Clean up leftovers')
            return 'Cleanup requests completed; runner sweep checks tagged leftovers'
          }
        )
      }
    })
  }
}
